import { GROUP_LABELS, PERMISSION_GROUPS } from './types';
import type { PermissionCapability, Role } from './types';

export interface MatrixColumn {
  code: string;
  name: string;
}

export interface MatrixCell {
  roleCode: string;
  roleName: string;
  has: boolean;
}

export interface MatrixRow {
  code: string;
  label: string;
  /** Una celda por rol, en el orden de `roles`; el rol va embebido (sin indexar cruzado). */
  cells: MatrixCell[];
}

export interface MatrixGroup {
  group: string;
  label: string;
  rows: MatrixRow[];
}

export interface PermissionMatrix {
  roles: MatrixColumn[];
  groups: MatrixGroup[];
}

const FALLBACK_GROUP_LABEL = 'Otras capacidades';

function groupLabel(group: string): string {
  return (GROUP_LABELS as Record<string, string | undefined>)[group] ?? FALLBACK_GROUP_LABEL;
}

/**
 * Orden de grupos: primero los conocidos (`PERMISSION_GROUPS`), luego cualquier
 * grupo desconocido en orden de primera aparición. Garantiza que un grupo nuevo
 * del backend NO oculte capacidades (H6): se muestran al final, nunca se pierden.
 */
function orderedGroups(catalog: PermissionCapability[]): string[] {
  const known = PERMISSION_GROUPS.filter((g) => catalog.some((c) => c.group === g));
  const unknown: string[] = [];
  for (const cap of catalog) {
    if (
      !PERMISSION_GROUPS.includes(cap.group as (typeof PERMISSION_GROUPS)[number]) &&
      !unknown.includes(cap.group)
    ) {
      unknown.push(cap.group);
    }
  }
  return [...known, ...unknown];
}

/**
 * Arma la matriz rol×capacidad para el render: columnas = roles (orden dado),
 * filas = capacidades del catálogo agrupadas por `group`. Cada celda refleja
 * `role.permissions.includes(code)`. Grupos conocidos primero, desconocidos al
 * final (sin ocultar nada); grupos sin capacidades se omiten. Lógica pura.
 */
export function buildPermissionMatrix(
  roles: Role[],
  catalog: PermissionCapability[],
): PermissionMatrix {
  const columns: MatrixColumn[] = roles.map((r) => ({ code: r.code, name: r.name }));

  const groups: MatrixGroup[] = [];
  for (const group of orderedGroups(catalog)) {
    const caps = catalog.filter((c) => c.group === group);
    if (caps.length === 0) {
      continue;
    }
    const rows: MatrixRow[] = caps.map((cap) => ({
      code: cap.code,
      label: cap.label,
      cells: roles.map((r) => ({
        roleCode: r.code,
        roleName: r.name,
        has: r.permissions.includes(cap.code),
      })),
    }));
    groups.push({ group, label: groupLabel(group), rows });
  }

  return { roles: columns, groups };
}
